import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Button, Dropdown, Menu } from 'antd';
import styled from 'styled-components';
import { useAuthStore } from '../../../../../../../store/auth/auth';
import { Device } from '../../../../../../../types/Device';
import { ONLINE_PLAY_URL } from '../../../../../../../const/const';
import './styles.css';
import { ReactComponent as SvgPoints } from 'utils/app/assets/icons/Points.svg';
import { ReactComponent as SvgSetting } from 'utils/app/assets/icons/Setting.svg';
import { ReactComponent as SvgSoundOn } from 'utils/app/assets/icons/Sound-on.svg';
import { ReactComponent as SvgSoundOff } from 'utils/app/assets/icons/Sound-off.svg';
import { useStateNameDevice } from '../../api/layout/useStateNameDevice';
import useRecordingStore from '../../api/recording/recordingStore';
import { defaultConfig, layoutConfigs } from './consts/configs';
import { arrayMove, rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { closestCenter, DndContext } from '@dnd-kit/core';
import SortableCameraTile from '../SortableCameraTile/SortableCameraTile';
import { AddDeviceModal } from '../modals/AddDeviceModal/AddDeviceModal';
import { LayoutType } from '../../../../../../../types/LayoutType';
import useNotification from '../../../../../../../utils/features/hooks/useNotification';
import useLayoutPositionsStore from '../../api/layoutPositions/layoutPositions';
import { RecordingTimer } from '../ModalCameraGrid/components/DeviceTime/components/RecordingTimer/RecordingTimer';
import { useSelectedLayout } from '../../../../../../../store/useSelectedLayout';
import { takePhotos } from '../../api/recording/recordingApi';
import { useNavigate } from 'react-router-dom';
import { useSelectedDevice } from '../../../../../../../store/devices/SelectedDevice';

// Интерфейсы для типизации
interface CameraTileProps {
    device?: Device;
    index: number;
    onMenuClick: (action: string, device: Device | null, index: number) => void;
    isShowNameDevice: boolean;
    onAddDevice: () => void;
    menuType: 'edit' | 'layout';
    style?: React.CSSProperties;
    setIsModalVisible: (open: boolean) => void;
    isPreview?: boolean;
    isSelected?: boolean;
    isSelectedItems?: boolean;
    isCurrentDevice?: boolean;
    onClick?: () => void;
    isDisabled?: boolean;
    tooltip?: string;
    dragListeners?: any;
    drag?: boolean;
    isDragging?: boolean;
    disableEmptySlots?: boolean;
    setSelectedDevice?: Dispatch<SetStateAction<Device | null>>;
    selectedLayout?: any;
    setDeleteDeviceModal?: (open: boolean) => void;
    changePosition?: boolean;
    startRecording?: (type: 'audio' | 'video', device: Device) => void;
    stopRecording?: (type: 'audio' | 'video', device: Device) => void;
    takePhoto?: (device: Device) => void;
    getDeviceRecordingType?: (deviceUID: string) => 'audio' | 'video' | 'photo' | null;
    isRecording?: boolean;
    recordingType?: 'audio' | 'video' | 'photo' | null;
}

interface CameraGridProps {
    viewType: string;
    devices: Device[];
    setDevices: (devices: Device[]) => void;
    menuType: 'edit' | 'layout';
    setIsModalVisible: (open: boolean) => void;
    onTileClick?: (position: number) => void;
    setSelectedDevice?: any;
    layout: LayoutType;
    getDeviceRecordingType?: (deviceUID: string) => 'audio' | 'video' | 'photo' | null;
}

// Компонент CameraTile
export const CameraTile: React.FC<CameraTileProps> = ({
    device,
    index,
    onAddDevice,
    menuType,
    style,
    setIsModalVisible,
    isSelectedItems = false,
    isPreview = false,
    isSelected = false,
    onClick,
    isDisabled,
    tooltip,
    drag = false,
    dragListeners,
    isDragging,
    disableEmptySlots = false,
    setDeleteDeviceModal,
    changePosition = false,
    startRecording,
    getDeviceRecordingType,
    stopRecording,
    takePhoto,
    // isRecording,
    // recordingType,
}) => {
    const { SmartDVRToken } = useAuthStore();
    const [isHovered, setIsHovered] = useState(false);
    const { isShowNameDevice } = useStateNameDevice();
    const { showNotification, contextHolderr } = useNotification();
    const [videoError, setVideoError] = useState(false);
    const navigate = useNavigate();
    const { setSelectedDevice } = useSelectedDevice();
    const { selectedLayout, setSelectedLayout } = useSelectedLayout();

    const shouldLoadVideo = SmartDVRToken && !videoError;

    useEffect(() => {
        setVideoError(false);
    }, [device?.UID, SmartDVRToken]);

    useEffect(() => {
        if (!SmartDVRToken) {
            setVideoError(true);
        }
    }, [SmartDVRToken]);

    const handleIframeError = () => {
        setVideoError(true);
    };

    const recordingType = device?.currentStatus?.includes('пишет аудио')
        ? 'audio'
        : device?.currentStatus?.includes('пишет видео')
            ? 'video'
            : null;

    const isRecording = !!recordingType;

    const handlePointerDown = (e: React.PointerEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest('[data-no-drag]')) {
            return;
        }
        dragListeners?.onPointerDown(e);
    };

    const { setPosition } = useLayoutPositionsStore();

    useEffect(() => {
        if (device?.UID) {
            setPosition(device.UID, index);
        }
    }, [device?.UID, index, setPosition]);

    // const recordingStatus = isRecording ? (
    //     <div className="recording-status">
    //         <span className={'dot'}></span>
    //         <span className={'body medium-bold'}>
    //             {recordingType === 'audio' ? 'Записывается аудио' : 'Записывается видео'}
    //         </span>
    //     </div>
    // ) : null;

    if (disableEmptySlots && !device) return null;

    return (
        <TileContainer
            style={{
                ...style,
                border: isSelected || isSelectedItems ? '4px solid var(--tertiary-dark)' : 'none',
                transition: drag ? 'transform 0.2s ease, opacity 0.2s ease' : 'none',
                position: 'relative',
                opacity: isDisabled ? 0.5 : 1,
                pointerEvents: isDisabled ? 'none' : 'auto',
                cursor: changePosition
                    ? 'pointer'
                    : drag
                        ? isDragging
                            ? 'grabbing'
                            : 'grab'
                        : 'default',
            }}
            onClick={isDisabled ? undefined : onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            isSelected={isSelectedItems}
            onPointerDown={dragListeners ? handlePointerDown : undefined}
        >
            {tooltip && <div className="recording-status-tooltip">{tooltip}</div>}
            {(isShowNameDevice || isHovered) && device && (
                <CameraHeader className={!isShowNameDevice && !isHovered ? 'hidden' : ''}>
                    <HeaderLeft>
                        <>
                            {/*{isRecording && (*/}
                            {/*    <>*/}
                            {/*        <div*/}
                            {/*            style={{*/}
                            {/*                position: 'relative',*/}
                            {/*                top: 1,*/}
                            {/*                right: -2,*/}
                            {/*                background: 'red',*/}
                            {/*                borderRadius: '50%',*/}
                            {/*                width: 8,*/}
                            {/*                height: 8,*/}
                            {/*                zIndex: 3,*/}
                            {/*            }}*/}
                            {/*        />*/}
                            {/*        /!*<span className={'body medium'} style={{ color: '#FFF' }}>*!/*/}
                            {/*        /!*    {recordingType === 'audio'*!/*/}
                            {/*        /!*        ? 'Идёт запись аудио'*!/*/}
                            {/*        /!*        : 'Идёт запись видео'}*!/*/}
                            {/*        /!*</span>*!/*/}
                            {/*    </>*/}
                            {/*)}*/}
                            <Circle
                                style={
                                    isRecording ? { color: 'white', backgroundColor: 'red' } : {}
                                }
                            >
                                {index + 1}
                            </Circle>
                            <DeviceName>{device?.name || 'Нет устройства'}</DeviceName>
                        </>
                    </HeaderLeft>

                    {device ? (
                        <Dropdown
                            placement={'bottomRight'}
                            overlay={menu(
                                device,
                                index,
                                menuType,
                                setIsModalVisible,
                                setSelectedDevice,
                                selectedLayout,
                                setSelectedLayout,
                                setDeleteDeviceModal,
                                startRecording,
                                stopRecording,
                                takePhoto,
                                getDeviceRecordingType,
                                showNotification,
                                navigate,
                            )}
                            trigger={['click']}
                            getPopupContainer={(triggerNode) =>
                                triggerNode.parentElement || document.body
                            }
                            data-no-drag
                            overlayClassName={
                                menuType === 'edit' ? 'dropdown-camera-edit' : 'dropdown-camera'
                            }
                        >
                            <Button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                }}
                                data-no-drag
                                className={
                                    'button-base button-type-secondary button-size-small_icon'
                                }
                                style={{
                                    position: 'relative',
                                    zIndex: 3,
                                    border: '2px solid var(--button-secondary-text)',
                                }}
                            >
                                <SvgPoints />
                            </Button>
                        </Dropdown>
                    ) : (
                        ''
                    )}
                </CameraHeader>
            )}

            {device ? (
                device.online ? (
                    shouldLoadVideo ? (
                        <>
                            <VideoIframe
                                src={`${ONLINE_PLAY_URL}${device.UID}/${SmartDVRToken}`}
                                allowFullScreen={true}
                            />
                            {/* Добавляем скрытый <img> для отлова ошибок */}
                            {/*<img* s / }
                            {/*    src={`${ONLINE_PLAY_URL}${device.UID}/${SmartDVRToken}`}*/}
                            {/*    onError={handleIframeError}*/}
                            {/*    style={{ display: 'none' }}*/}
                            {/*    alt=""*/}
                            {/*/>*/}
                        </>
                    ) : (
                        <OfflineMessage>
                            <span>Ошибка загрузки видео</span>
                            {!SmartDVRToken && <span>Требуется авторизация</span>}
                        </OfflineMessage>
                    )
                ) : (
                    <OfflineMessage>Устройство оффлайн</OfflineMessage>
                )
            ) : (
                <EmptyTile
                    onClick={() => {
                        onAddDevice();
                    }}
                    data-no-drag
                >
                    +
                </EmptyTile>
            )}

            {/* Условие для отображения футера */}
            {/*{!isPreview && device && (*/}
            {/*    <CameraFooter className={!isShowNameDevice && !isHovered ? 'hidden' : ''}>*/}
            {/*        /!*<div></div>*!/*/}
            {/*        /!*<FooterIcon onClick={() => console.log('Left icon clicked')} data-no-drag>*!/*/}
            {/*        /!*{audio ? (*!/*/}
            {/*        /!*    <SvgSoundOn*!/*/}
            {/*        /!*        style={{ paddingLeft: 8 }}*!/*/}
            {/*        /!*        onClick={() => setAudio(false)}*!/*/}
            {/*        /!*    />*!/*/}
            {/*        /!*) : (*!/*/}
            {/*        /!*    <SvgSoundOff*!/*/}
            {/*        /!*        style={{ paddingLeft: 8, fill: '#FFF' }}*!/*/}
            {/*        /!*        onClick={() => setAudio(true)}*!/*/}
            {/*        /!*    />*!/*/}
            {/*        /!*)}*!/*/}
            {/*        /!*</FooterIcon>*!/*/}

            {/*        /!*{isRecording && (*!/*/}
            {/*        /!*    <div className="recording-status" style={{ textAlign: 'center' }}>*!/*/}
            {/*        /!*        <div*!/*/}
            {/*        /!*            style={{*!/*/}
            {/*        /!*                position: 'absolute',*!/*/}
            {/*        /!*                top: 8,*!/*/}
            {/*        /!*                right: 8,*!/*/}
            {/*        /!*                background: 'red',*!/*/}
            {/*        /!*                borderRadius: '50%',*!/*/}
            {/*        /!*                width: 12,*!/*/}
            {/*        /!*                height: 12,*!/*/}
            {/*        /!*                zIndex: 3,*!/*/}
            {/*        /!*            }}*!/*/}
            {/*        /!*            title={recordingType === 'audio' ? 'Запись аудио' : 'Запись видео'}*!/*/}
            {/*        /!*        />*!/*/}
            {/*        /!*        /!*{recordingStatus}*!/*!/*/}
            {/*        /!*        /!*{recordingType === 'audio' ? (*!/*!/*/}
            {/*        /!*        /!*    <>*!/*!/*/}
            {/*        /!*        /!*        <span className={'dot'}></span>{' '}*!/*!/*/}
            {/*        /!*        /!*        <span className={'body medium-bold'}>*!/*!/*/}
            {/*        /!*        /!*            Записывается аудио*!/*!/*/}
            {/*        /!*        /!*          *!/*!/*/}
            {/*        /!*        /!*        </span>*!/*!/*/}
            {/*        /!*        /!*    </>*!/*!/*/}
            {/*        /!*        /!*) : (*!/*!/*/}
            {/*        /!*        /!*    <>*!/*!/*/}
            {/*        /!*        /!*        <span className={'dot'}></span>*!/*!/*/}
            {/*        /!*        /!*        <span className={'body medium-bold'}>*!/*!/*/}
            {/*        /!*        /!*            Записывается видео*!/*!/*/}
            {/*        /!*        /!*     *!/*!/*/}
            {/*        /!*        /!*        </span>*!/*!/*/}
            {/*        /!*        /!*    </>*!/*!/*/}
            {/*        /!*        /!*)}*!/*!/*/}
            {/*        /!*    </div>*!/*/}
            {/*        /!*)}*!/*/}

            {/*        <FooterIcon onClick={() => console.log('Right icon clicked')}>*/}
            {/*            <SvgSetting style={{ paddingRight: 8 }} />*/}
            {/*        </FooterIcon>*/}
            {/*    </CameraFooter>*/}
            {/*)}*/}
            {contextHolderr}
        </TileContainer>
    );
};

export const CameraGrid: React.FC<CameraGridProps> = ({
    viewType,
    devices,
    setDevices,
    menuType,
    setIsModalVisible,
    onTileClick,
    setSelectedDevice,
    layout,
}) => {
    const config = layoutConfigs[viewType] || defaultConfig;
    const { isShowNameDevice } = useStateNameDevice();
    const [addDeviceModal, setAddDeviceModal] = useState(false);
    const { startRecording, stopRecording } = useRecordingStore();
    const { showNotification, contextHolderr } = useNotification();
    const { selectedLayout, setSelectedLayout } = useSelectedLayout();
    console.log(addDeviceModal);

    // const updateDeviceStatus = (deviceUID: string, status: string) => {
    //     setDevices(prevDevices =>
    //         prevDevices.map(device =>
    //             device.UID === deviceUID
    //                 ? { ...device, currentStatus: status }
    //                 : device
    //         )
    //     );
    // };

    const handleAddDevice = () => {
        // setDevices(updatedDevices.map(uid => ({ UID: uid })));
        // setAddDeviceModal(false);
    };

    const handleDragEnd = (event: any) => {
        const { active, over } = event;

        const target = event.activatorEvent?.target as HTMLElement;
        if (target?.closest('[data-no-drag]')) {
            return;
        }

        if (active.id !== over?.id) {
            const oldIndex = devices.findIndex((d) => d.UID === active.id);
            const newIndex = devices.findIndex((d) => d.UID === over?.id);

            const newDevices = arrayMove(devices, oldIndex, newIndex);
            setDevices(newDevices);
        }
    };

    const handleStopRecording = async (type: 'audio' | 'video', device: Device) => {
        await stopRecording(type, [device]);
        // updateDeviceStatus(device.UID, type === 'audio' ? 'пишет аудио' : 'пишет видео');
        showNotification(
            'success',
            'Файлы записаны',
            `Сохранены ${type === 'audio' ? 'аудио' : 'видео'}`,
        );
    };

    const handleStartRecording = async (type: 'audio' | 'video', device: Device) => {
        await startRecording(type, [device]);
        // updateDeviceStatus(device.UID, type === 'audio' ? 'пишет аудио' : 'пишет видео');
        showNotification(
            'warning',
            'Запись файла',
            `Ведется запись ${type === 'audio' ? 'аудио' : 'видео'} с "${device?.name}"`,
        );
    };

    const handleTakePhoto = async (device: Device) => {
        showNotification('success', 'Файлы записаны', `Сохранено фото с ${device.name}`);
    };

    return (
        <>
            {menuType === 'edit' ? (
                <GridContainer $cols={config.cols} $rows={config.rows}>
                    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext
                            items={devices.map((d) => d.UID)}
                            strategy={rectSortingStrategy}
                        >
                            {config.cameras.map((cameraSlot, index) => {
                                const device = devices[index];
                                const pos = config.cameras[index] || config.cameras[0];
                                return (
                                    <>
                                        <SortableCameraTile
                                            key={device?.UID || `empty-${index}`}
                                            id={device?.UID || `empty-${index}`}
                                            device={device}
                                            index={index}
                                            x={cameraSlot.x}
                                            y={cameraSlot.y}
                                            width={cameraSlot.width}
                                            height={cameraSlot.height}
                                            onAddDevice={() => setAddDeviceModal(true)}
                                            cols={config.cols}
                                            rows={config.rows}
                                            setIsModalVisible={setIsModalVisible}
                                            setSelectedDevice={setSelectedDevice}
                                        />
                                    </>
                                );
                            })}
                        </SortableContext>
                    </DndContext>
                </GridContainer>
            ) : (
                <StaticGrid cols={config.cols}>
                    {config.cameras.map((camera, index) => {
                        const device = devices[index];
                        return (
                            <CameraTile
                                key={index}
                                device={device}
                                index={index}
                                menuType={menuType}
                                onMenuClick={(action, device, idx) =>
                                    console.log(action, device, idx)
                                }
                                onAddDevice={() => {
                                    setAddDeviceModal(true);
                                }}
                                isShowNameDevice={isShowNameDevice}
                                setIsModalVisible={setIsModalVisible}
                                onClick={() => {
                                    onTileClick?.(index);
                                }}
                                startRecording={handleStartRecording}
                                stopRecording={handleStopRecording}
                                takePhoto={handleTakePhoto}
                                style={{
                                    gridColumn: `${camera.x + 1}/span ${camera.width}`,
                                    gridRow: `${camera.y + 1}/span ${camera.height}`,
                                }}
                            />
                        );
                    })}
                </StaticGrid>
            )}

            <AddDeviceModal
                layout={layout}
                existingDevices={layout?.devices?.map((device) => device.UID)}
                title={'Добавить устройство'}
                onCancel={() => setAddDeviceModal(false)}
                setIsModalVisible={setAddDeviceModal}
                onOk={() => handleAddDevice()}
                visible={addDeviceModal}
                layoutBool={true}
            />
        </>
    );
};

const TileContainer = styled.div<{ isSelected: boolean }>`
    background: var(--gray-01);
    border-radius: 8px;
    overflow: hidden;
    border: ${({ isSelected }) => (isSelected ? '4px solid var(--tertiary-dark)' : 'none')};
    cursor: pointer;
    display: flex;
    flex-direction: column;
    // min-height: 0; // Важно для корректного отображения
    height: 100%; // Фиксируем высоту
    // height: auto;
    [data-no-drag] {
        pointer-events: auto;
        touch-action: auto;
    }

    /* Стиль для перетаскивания */
    [data-drag-handle] {
        cursor: grab;
    }

    [data-drag-handle]:active {
        cursor: grabbing;
    }
`;

const CameraHeader = styled.div`
    position: absolute;
    height: 20px;
    border-radius: 8px 8px 0 0;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px;
    background: var(--primary-shadow-1);
    z-index: 2;

    button {
        cursor: pointer;
        pointer-events: auto;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CameraFooter = styled.div`
    height: 20px;
    position: absolute;
    border-radius: 0 0 8px 8px;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: end;
    align-items: center;
    padding: 6px;
    background: var(--primary);
    // z-index: 1;
`;

const FooterIcon = styled.div`
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    color: #fff;

    &:hover {
        opacity: 0.8;
    }
`;

const Circle = styled.div`
    width: 20px;
    height: 20px;
    background: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
`;

const DeviceName = styled.span`
    color: #fff;
    font-size: 14px;
`;

const VideoIframe = styled.iframe`
    width: 100%;
    height: 100%;
    border: none;
`;

const OfflineMessage = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #fff;
`;

const EmptyTile = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    // background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    color: white;
    font-size: 24px;
    cursor: pointer;
    transition: all 0.2s ease;
    pointer-events: all;
    z-index: 2;
    &:hover {
        background: rgba(255, 255, 255, 0.2);
    }
`;

const GridContainer = styled.div<{ $cols: number; $rows: number }>`
    display: grid;
    grid-template-columns: repeat(${(p) => p.$cols}, 1fr);
    grid-auto-rows: minmax(100px, 1fr);
    gap: 8px;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    position: relative;
`;

const StaticGrid = styled.div<{ cols: number }>`
    display: grid;
    width: 100%;
    height: 100%;
    grid-template-columns: repeat(${(p) => p.cols}, 1fr);
    grid-auto-rows: minmax(100px, 1fr);
    gap: 8px;
`;

// Вспомогательная функция для меню
const menu = (
    device: Device,
    index: number,
    menuType: 'edit' | 'layout',
    setIsModalVisible: (open: boolean) => void,
    setSelectedDevice?: any,
    selectedLayout?: any,
    setSelectedLayout?: any,
    setDeleteDeviceModal?: (open: boolean) => void,
    startRecording?: (type: 'audio' | 'video', device: Device) => void,
    stopRecording?: (type: 'audio' | 'video', device: Device) => void,
    takePhoto?: (device: Device) => void,
    getDeviceRecordingType?: (deviceUID: string) => 'audio' | 'video' | 'photo' | null,
    showNotification?: (
        type: 'success' | 'error' | 'info' | 'warning',
        title: string,
        message: string,
    ) => void,
    navigate?: any,
) => {
    if (!device) return <Menu />;
    const recordingType = getDeviceRecordingType?.(device.UID);
    // const { selectedLayout, setSelectedLayout } = useSelectedLayout();

    const handleStartRecording = async (type: 'audio' | 'video', device: Device) => {
        if (!device.online) {
            showNotification?.('error', 'Ошибка', 'Устройство оффлайн');
            return false;
        }

        try {
            await startRecording?.(type, device);

            const updatedDevices = selectedLayout?.devices?.map((d: Device) =>
                d.UID === device.UID
                    ? { ...d, currentStatus: type === 'audio' ? 'пишет аудио' : 'пишет видео' }
                    : d,
            );

            setSelectedLayout({
                ...selectedLayout,
                devices: updatedDevices,
            });

            showNotification?.(
                'warning',
                'Запись файла',
                `Ведется запись ${type === 'audio' ? 'аудио' : 'видео'} с "${device.name}"`,
            );
        } catch (error) {
            showNotification?.(
                'error',
                'Ошибка',
                `Не удалось начать запись ${type === 'audio' ? 'аудио' : 'видео'}`,
            );
        }
    };

    const handleDeviceClick = (device: Device) => {
        if (device) {
            navigate(`/device/${device.name}`);
            setSelectedDevice(device);
        }
    };

    const handleStopRecording = async (type: 'audio' | 'video') => {
        try {
            await stopRecording?.(type, device);

            const updatedDevices = selectedLayout?.devices?.map((d: Device) =>
                d.UID === device.UID ? { ...d, currentStatus: '' } : d,
            );

            setSelectedLayout({
                ...selectedLayout,
                devices: updatedDevices,
            });
            showNotification?.(
                'success',
                'Файлы записаны',
                `Сохранены ${type === 'audio' ? 'аудио' : 'видео'}`,
            );
        } catch (error) {
            showNotification?.('error', 'Ошибка', `Не удалось остановить запись ${type}`);
        }
    };

    const handleTakePhoto = async () => {
        try {
            if (!device.online) {
                showNotification?.('error', 'Ошибка', 'Устройство оффлайн');
                return;
            }
            await takePhotos([device.UID]);

            showNotification?.('success', 'Фото сделано', `Сохранено фото с ${device.name}`);
        } catch (error) {
            showNotification?.('error', 'Ошибка', 'Не удалось сделать фото');
        }
    };
    return (
        <>
            <Menu data-no-drag style={{ cursor: 'pointer' }}>
                {menuType === 'edit' ? (
                    <div>
                        <Menu.Item
                            data-no-drag
                            key="edit"
                            onClick={() => {
                                setIsModalVisible(true);
                                setSelectedDevice?.(device);
                            }}
                        // className={'menu-item-edit'}
                        >
                            Изменить положение
                        </Menu.Item>
                        <Menu.Item
                            key="delete"
                            danger
                            data-no-drag
                            onClick={
                                setDeleteDeviceModal
                                    ? () => {
                                        setDeleteDeviceModal(true);
                                    }
                                    : () => { }
                            }
                        // className={'menu-item-delete'}
                        >
                            Удалить с раскладки
                        </Menu.Item>
                    </div>
                ) : (
                    <>
                        {device.currentStatus == 'пишет видео' ? (
                            <Menu.Item
                                key="stopVideo"
                                onClick={() => handleStopRecording('video')}
                                danger
                            >
                                Остановить запись видео
                            </Menu.Item>
                        ) : (
                            <Menu.Item
                                key="recVideo"
                                onClick={() => handleStartRecording('video', device)}
                                disabled={!device.online}
                            >
                                Запись видео
                            </Menu.Item>
                        )}

                        {device.currentStatus == 'пишет аудио' ? (
                            <Menu.Item
                                key="stopAudio"
                                onClick={() => handleStopRecording('audio')}
                                danger
                            >
                                Остановить запись аудио
                            </Menu.Item>
                        ) : (
                            <Menu.Item
                                key="recAudio"
                                onClick={() => handleStartRecording('audio', device)}
                                disabled={!device.online || device.currentStatus == 'пишет видео'}
                            >
                                Запись аудио
                            </Menu.Item>
                        )}

                        <Menu.Item
                            key="takePhoto"
                            onClick={() => handleTakePhoto()}
                            disabled={!device.online}
                        >
                            Сделать фото
                        </Menu.Item>

                        <Menu.Item onClick={() => handleDeviceClick(device)} key="goToDevice">
                            Перейти к устройству
                        </Menu.Item>
                    </>
                )}
            </Menu>
        </>
    );
};
